import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Pagination from "@mui/material/Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import Inbox from "@mui/icons-material/Inbox";
import { PiRulerLight, PiScalesLight } from "react-icons/pi";
import SearchIcon from "../../../../../../../assets/icons/dispatcher-search.svg";
import Tooltip from "../../../../../../../Components/Tooltip/Tooltip";
import { findSettingByType } from "../../../../../../../helpers/helpers";
import { hasSize, onePressOnly } from "./ProductCells";
import "./GridParts.css";

// The parts of the My Products grids (Products, Bundles and Packages): the
// data and paging hook, the search box, one tile look, the list's footer and
// its empty state. Each grid view keeps only its request and its tiles.

// Tile geometry, as in GridParts.css: the grid's minimum column width and
// gap, its padding, and the fixed tile height.
const TILE_MIN = 250;
const TILE_H = 118;
const GAP = 8;
const PAD_X = 6;
const PAD_Y = 13;
const FOOT_H = 28;

// The grids show one page of exactly the tiles that fit their box (columns x
// rows, no scrolling inside), so the page size follows the screen. Page and
// page size change together, so a resize asks for one page, keeping the first
// tile that was showing. On phones, or before the box has a size (tests), the
// page size is `fallback` (the list's: the user's "perPage" setting, else 20)
// and the box scrolls. `perPage` is null until the box is measured.
export function useGridPaging(fallback) {
  const [area, setArea] = useState(null);
  const [paging, setPaging] = useState({ page: 1, perPage: null });
  // Space above the rows, so a full page sits in the middle of the box; every
  // page starts at the same height (a short last page doesn't jump). null
  // when the page size is the fallback (the box scrolls).
  const [offset, setOffset] = useState(null);

  useLayoutEffect(() => {
    if (!area) return undefined;
    const measure = () => {
      const phone = window.matchMedia?.("(max-width: 600px)").matches;
      const footer = area.querySelector(".myprod__gridfoot");
      // Unrounded sizes, as the CSS grid lays the tiles out with them
      // (clientWidth rounds: a 771.6px box said 772, three columns where CSS
      // draws two, so a page held a row too many and scrolled).
      const box = area.getBoundingClientRect();
      const width = box.width;
      const height = box.height - (footer ? footer.getBoundingClientRect().height : FOOT_H);
      let fit = fallback;
      let top = null;
      if (!phone && width > 0 && height > 0) {
        const cols = Math.max(1, Math.floor((width - PAD_X + GAP) / (TILE_MIN + GAP)));
        const rows = Math.max(1, Math.floor((height - PAD_Y + GAP) / (TILE_H + GAP)));
        fit = cols * rows;
        top = Math.max(3, Math.floor((height - (rows * TILE_H + (rows - 1) * GAP)) / 2));
      }
      setOffset(top);
      setPaging((current) =>
        current.perPage === fit
          ? current
          : {
              perPage: fit,
              page: current.perPage
                ? Math.floor(((current.page - 1) * current.perPage) / fit) + 1
                : current.page,
            }
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    let timer;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(measure, 150);
    });
    observer.observe(area);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [area, fallback]);

  return {
    areaRef: setArea,
    offset,
    page: paging.page,
    perPage: paging.perPage,
    setPage: (page) => setPaging((current) => ({ ...current, page })),
  };
}

// The photo, or `icon` when there is none or it fails to load.
function TilePhoto({ url, icon }) {
  const [failedUrl, setFailedUrl] = useState(null);
  return (
    <span className="myprod__tile-img" aria-hidden="true">
      {url && failedUrl !== url ? (
        <img src={url} alt="" loading="lazy" onError={() => setFailedUrl(url)} />
      ) : (
        icon
      )}
    </span>
  );
}

// One page of a grid's rows: the request, the search (its box goes to the
// scroller's row through setFilter, as before), the page and page size
// (useGridPaging), and what the shell shows: skeleton tiles while another
// page or size loads (a search keeps the tiles and the box's spinner), the
// empty message only after a load. fetchPage(page, perPage, search) is the
// grid's service call; refresh (bundles / packages) asks again.
export function useGridData({ fetchPage, refresh, setFilter, placeholder }) {
  const user = useSelector((state) => state.user);
  const paging = useGridPaging(
    Number(findSettingByType(user?.general_settings, "perPage")?.value) || 20
  );
  const { page, perPage } = paging;
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(0);
  // The page and size whose tiles are showing.
  const [loadedKey, setLoadedKey] = useState(null);
  // The page size of the latest request: an answer for an older size (the
  // box was resized while it loaded, and the new size was asked for) is
  // dropped, so a page never lands in a box it doesn't fit.
  const askedSize = useRef(null);

  useEffect(() => {
    if (!perPage) return;
    askedSize.current = perPage;
    const load = async () => {
      setLoading(true);
      const response = await fetchPage(page, perPage, search);
      if (askedSize.current !== perPage) return;
      setLoadedKey(`${page}:${perPage}`);
      if (!response.error) {
        const result = response?.data?.result;
        setItems(result?.data ?? []);
        setTotal(result?.total ?? 0);
        setLastPage(Math.ceil(result?.total / result?.per_page));
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, search, refresh]);

  useEffect(() => {
    setFilter(
      loading || search !== "" || lastPage > 0 ? (
        <GridSearch
          value={search}
          onChange={setSearch}
          placeholder={placeholder}
          loading={loading}
        />
      ) : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, search, lastPage]);

  return {
    ...paging,
    items,
    total,
    lastPage,
    skeleton:
      (loading || loadedKey === null) && loadedKey !== `${page}:${perPage}`
        ? perPage || 0
        : 0,
    empty: loadedKey !== null && !loading && lastPage === 0,
  };
}

// The grid's search box: a pill with an icon and the loading spinner.
// `hidden`: the same box, invisible, holding its place (LibraryFrame).
export function GridSearch({ value = "", onChange, placeholder, loading, hidden }) {
  return (
    <div
      className={`myprod__search${hidden ? " is-hidden" : ""}`}
      aria-hidden={hidden || undefined}
    >
      <img className="myprod__search-ic" src={SearchIcon} alt="" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        readOnly={hidden}
        tabIndex={hidden ? -1 : undefined}
        placeholder={placeholder}
        className="myprod__search-input"
      />
      {loading && <CircularProgress size={12} style={{ color: "#C1C1C1" }} />}
    </div>
  );
}

// The tiles' Size and Weight values. A size not above 0 on every side says
// "Not set" (amber, with a hover note), as the lists do.
export function sizeSpec({ row, unit, kind, badge }) {
  const sized = hasSize(row?.length, row?.width, row?.height);
  return {
    key: "size",
    icon: <PiRulerLight aria-hidden="true" />,
    label: "Size",
    badge,
    value: sized ? `${row.length}×${row.width}×${row.height}` : "Not set",
    unit: sized ? unit : undefined,
    missing: !sized,
    title: sized
      ? `${row.length} × ${row.width} × ${row.height}${unit ? ` ${unit}` : ""}`
      : `No size saved for this ${kind} yet`,
  };
}

export const weightSpec = (value, unit) => ({
  key: "weight",
  icon: <PiScalesLight aria-hidden="true" />,
  label: "Weight",
  value,
  unit,
});

// One tile. The whole tile is the button: a click (or Enter / Space) calls
// onClick, as a click on the old card did. `blocked` keeps the old "no
// location yet" look: a not-allowed cursor and the "Select a location" hint
// (the click still goes through, so the page can say why).
// specs: [{ key, icon, label, badge, value, unit, title, missing, tone }]
export function GridTile({ name, sub, photo, icon, specs, onClick, blocked }) {
  const shown = name || "-";
  const tile = (
    <button
      type="button"
      className={`myprod__tile${blocked ? " is-blocked" : ""}`}
      aria-label={name || "No name"}
      onKeyDown={onePressOnly}
      onClick={(e) => {
        // A mouse click leaves no focus on the tile (as the old card, which
        // could not take focus), so a later Space / Enter can't add it again.
        if (e.detail > 0) e.currentTarget.blur();
        onClick();
      }}
    >
      <span className="myprod__tile-head">
        <TilePhoto url={photo} icon={icon} />
        <span className="myprod__tile-name">
          <b title={name || undefined}>{shown}</b>
          {sub !== undefined && <span className="myprod__tile-sub">{sub}</span>}
        </span>
      </span>
      <span
        className="myprod__tile-specs"
        style={{ gridTemplateColumns: `repeat(${specs.length}, minmax(0, 1fr))` }}
      >
        {specs.map((spec) => (
          <span
            key={spec.key}
            className={`myprod__spec${spec.missing ? " is-missing" : ""}${
              spec.tone ? ` is-${spec.tone}` : ""
            }`}
            title={spec.title}
          >
            <span className="myprod__spec-k">
              {spec.icon}
              {spec.label}
              {spec.badge}
            </span>
            <span className="myprod__spec-v">
              <span className="myprod__spec-n">{spec.value}</span>
              {spec.unit && <small>{spec.unit}</small>}
            </span>
          </span>
        ))}
      </span>
    </button>
  );
  return blocked ? <Tooltip title="Select a location">{tile}</Tooltip> : tile;
}

// A grey placeholder in a tile's shape, while a page loads.
function SkeletonTile() {
  return (
    <span className="myprod__tile is-skeleton">
      <span className="myprod__tile-head">
        <span className="myprod__sk myprod__sk-img" />
        <span className="myprod__tile-name">
          <span className="myprod__sk myprod__sk-line" />
          <span className="myprod__sk myprod__sk-line is-short" />
        </span>
      </span>
      <span className="myprod__sk myprod__sk-specs" />
    </span>
  );
}

// A fitted page: the rows centred (offset above, the rest below, no bottom
// padding, so they never scroll). Otherwise the stylesheet's padding.
const gridPadding = (offset) =>
  offset === null ? undefined : { paddingTop: offset, paddingBottom: 0 };

// The grid's area and the footer under it, from useGridData's result: the
// tiles (children), skeleton tiles while a page loads, or the empty message.
export function GridShell({ grid, emptyText, children }) {
  const { areaRef, offset, skeleton, empty, page, lastPage, total, setPage } = grid;
  return (
    <div className="myprod__gridshell" ref={areaRef}>
      <div className="myprod__grid-scroll">
        {skeleton > 0 ? (
          <div
            className="myprod__grid"
            style={gridPadding(offset)}
            aria-busy="true"
            aria-label="Loading"
          >
            {Array.from({ length: skeleton }, (_, index) => (
              <SkeletonTile key={index} />
            ))}
          </div>
        ) : empty ? (
          <div className="myprod__grid-empty">
            <Inbox className="myprod__grid-empty-ic" />
            <div>{emptyText}</div>
          </div>
        ) : (
          <div className="myprod__grid" style={gridPadding(offset)}>
            {children}
          </div>
        )}
      </div>
      {lastPage > 0 && (
        <GridFooter page={page} lastPage={lastPage} total={total} onPage={setPage} />
      )}
    </div>
  );
}

// BoltTable's footer look (same Pagination settings) with the total; no
// rows-per-page menu, the page size follows the box.
export function GridFooter({ page, lastPage, total, onPage }) {
  return (
    <div className="myprod__gridfoot">
      <Pagination
        count={lastPage || 1}
        page={page}
        onChange={(event, value) => onPage(value)}
        size="small"
        showFirstButton
        showLastButton
        siblingCount={0}
        boundaryCount={1}
        className="myprod__pager"
      />
      <span className="myprod__total">Total: {total || 0}</span>
    </div>
  );
}
